import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { garantirUsuario } from '@/lib/usuario';
import { guardarDestino } from '@/lib/destino';
import { lerInscricaoCodigo } from '@/lib/inscricaoCodigo';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { mascaraCEP, buscarEnderecoPorCep } from '@/lib/cep';
import type { Participacao } from '@/types/evento-publico';
import { textoDoValor } from '@/pages/evento/utils';
import { dividirNomeGrupo, ehGrupoEmDuasEtapas, sexosDaCidade } from '@/lib/grupoOpcao';
import type { MeuPerfil } from '@/pages/meu-perfil/types';
import BarraProgresso from './components/BarraProgresso';
import CartaoResumo from './components/CartaoResumo';
import CartaoQuemVai from './components/CartaoQuemVai';
import CartaoPessoa from './components/CartaoPessoa';
import CartaoGrupo from './components/CartaoGrupo';
import CartaoConferencia from './components/CartaoConferencia';
import {
  EMAIL_REGEX,
  dataParaISO,
  formatarNascimento,
  mascaraCPF,
  mascaraData,
  mascaraTelefone,
  validarCPF,
} from './utils';
import type { PessoaForm, PessoaSalva } from './types';

const FORM_VAZIO: PessoaForm = {
  nome: '',
  cpf: '',
  nascimento: '',
  telefone: '',
  email: '',
  cep: '',
  endereco: '',
  complemento: '',
  bairro: '',
  cidade: '',
  uf: '',
};

export default function InscricaoPage() {
  const { modalidadeId } = useParams<{ modalidadeId: string }>();
  const [searchParams] = useSearchParams();
  const opcaoParam = searchParams.get('opcao');
  const navigate = useNavigate();
  const { user, pronto } = useAuth();

  const [uid, setUid] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [partes, setPartes] = useState<Participacao[]>([]);
  const [eventoNome, setEventoNome] = useState('');
  const [eventoSlug, setEventoSlug] = useState('');
  const [temFormulario, setTemFormulario] = useState(false);

  const [modo, setModo] = useState<'eu' | 'outra' | null>(null);
  const [pessoas, setPessoas] = useState<PessoaSalva[]>([]);
  const [euParticipante, setEuParticipante] = useState<PessoaSalva | null>(null);
  const [pessoaSelecionada, setPessoaSelecionada] = useState<PessoaSalva | null>(null);
  const [novoCadastro, setNovoCadastro] = useState(false);
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState<PessoaForm>(FORM_VAZIO);
  const [grupoId, setGrupoId] = useState<number | null>(null);
  // Cidade escolhida no 1º passo quando os grupos são "Cidade · Sexo".
  const [cidade, setCidade] = useState<string | null>(null);

  // Aviso simples acima do Continuar quando falta algo.
  const [avisoFalta, setAvisoFalta] = useState<string | null>(null);
  // Bloco com borda vermelha por 2 s (o que faltou).
  const [destaque, setDestaque] = useState<string | null>(null);
  const timerDestaque = useRef<number | null>(null);
  // Trava contra dois cliques seguidos no Continuar (o botão nunca fica disabled).
  const enviando = useRef(false);

  const [salvando, setSalvando] = useState(false);
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [inscricaoDuplicada, setInscricaoDuplicada] = useState<string | null>(null);
  const [erroCampos, setErroCampos] = useState<Record<string, string>>({});
  const [perfilIncompleto, setPerfilIncompleto] = useState(false);

  // Endereço aparece para "Eu mesmo" incompleto e sempre para "Outra pessoa".
  const mostrarEndereco = (perfilIncompleto && modo === 'eu') || modo === 'outra';

  useEffect(() => {
    let ativo = true;
    async function carregar() {
      setLoading(true);
      setErro(null);
      try {
        if (!pronto) return;
        if (!user) {
          const destino = `/inscricao/${modalidadeId}${opcaoParam ? `?opcao=${opcaoParam}` : ''}`;
          guardarDestino(destino);
          navigate(`/entrar?voltar=${encodeURIComponent(destino)}`, { replace: true });
          return;
        }
        if (ativo) setUid(user.id);
        await garantirUsuario();

        const { data: partesData, error: errPartes } = await supabase
          .from('vw_participacoes')
          .select('*')
          .eq('modalidade_id', Number(modalidadeId));
        if (errPartes) throw errPartes;

        const lista = (partesData as Participacao[]) || [];
        if (lista.length === 0) {
          if (ativo) setErro('Não encontramos esta forma de participar.');
          return;
        }
        if (ativo) setPartes(lista);

        // A opção marcada na página do evento vem em ?opcao= (a pessoa já
        // escolheu Masculino/Feminino antes do "Quero ir"): abre já marcada,
        // sem perguntar duas vezes. Sem o parâmetro, um grupo único já vem
        // marcado e com 2+ grupos a pessoa escolhe aqui.
        const opcoes = lista.filter((p) => p.opcao_id !== null);
        const doLink = opcaoParam
          ? opcoes.find((p) => String(p.opcao_id) === opcaoParam && p.situacao === 'abertas')
          : null;
        if (ativo) {
          if (doLink) setGrupoId(doLink.opcao_id);
          else if (opcoes.length === 1) setGrupoId(opcoes[0].opcao_id);
        }

        const eventoId = lista[0].evento_id;
        const { data: ev } = await supabase
          .from('eventos')
          .select('nome, slug')
          .eq('id', eventoId)
          .maybeSingle();
        if (ativo) {
          setEventoNome(ev?.nome || '');
          setEventoSlug(ev?.slug || '');
        }

        const { data: formData } = await supabase
          .from('formularios')
          .select('id')
          .eq('modalidade_id', Number(modalidadeId))
          .eq('ativo', true)
          .maybeSingle();
        if (ativo) setTemFormulario(!!formData);
      } catch (e) {
        if (ativo) {
          setErro(mensagemAmigavel(e, 'Não foi possível carregar. Tente novamente.'));
        }
      } finally {
        if (ativo) setLoading(false);
      }
    }
    carregar();
    return () => {
      ativo = false;
    };
  }, [modalidadeId, opcaoParam, navigate, pronto, user]);

  // Busca o endereço pelo CEP enquanto preenche ("eu" incompleto ou "outra pessoa").
  useEffect(() => {
    if (!mostrarEndereco) return;
    if (form.cep.replace(/\D/g, '').length !== 8) return;

    let ativo = true;
    buscarEnderecoPorCep(form.cep).then((end) => {
      if (!ativo || !end) return;
      setForm((f) => ({
        ...f,
        endereco: f.endereco || end.logradouro,
        bairro: f.bairro || end.bairro,
        cidade: f.cidade || end.localidade,
        uf: f.uf || end.uf,
      }));
    });
    return () => {
      ativo = false;
    };
  }, [form.cep, mostrarEndereco]);

  const temGrupos = useMemo(() => partes.some((p) => p.opcao_id !== null), [partes]);
  const grupos = useMemo(() => partes.filter((p) => p.opcao_id !== null), [partes]);

  const grupoSelecionado = useMemo(
    () => (temGrupos ? partes.find((p) => p.opcao_id === grupoId) : null),
    [partes, temGrupos, grupoId],
  );

  const duasEtapas = useMemo(() => ehGrupoEmDuasEtapas(grupos), [grupos]);

  // Um grupo já marcado (link ?opcao=) também marca a cidade dele.
  useEffect(() => {
    if (!duasEtapas) return;
    const cidadeDoGrupo = dividirNomeGrupo(grupoSelecionado?.grupo ?? null)?.cidade ?? null;
    if (cidadeDoGrupo && cidadeDoGrupo !== cidade) setCidade(cidadeDoGrupo);
  }, [duasEtapas, grupoSelecionado, cidade]);

  useEffect(
    () => () => {
      if (timerDestaque.current) window.clearTimeout(timerDestaque.current);
    },
    [],
  );

  // Rola até o item que falta e põe borda vermelha no bloco por 2 segundos.
  function destacar(alvo: string, bloco: string) {
    setDestaque(bloco);
    if (timerDestaque.current) window.clearTimeout(timerDestaque.current);
    timerDestaque.current = window.setTimeout(() => setDestaque(null), 2000);
    window.setTimeout(() => {
      const el = document.getElementById(alvo);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 90);
  }

  function escolherCidade(nova: string) {
    setCidade(nova);
    setAvisoFalta(null);
  }

  function escolherGrupo(id: number | null) {
    setGrupoId(id);
    setAvisoFalta(null);
  }

  const valorExibido = useMemo(() => {
    if (temGrupos) {
      return grupoSelecionado ? textoDoValor(grupoSelecionado) : '—';
    }
    return partes.length ? textoDoValor(partes[0]) : '—';
  }, [partes, temGrupos, grupoSelecionado]);

  // "Ver detalhes do evento": volta para a página do evento SEM cair de novo
  // na inscrição (?ver=1). Leva o código do convite quando ele ainda vale.
  const linkDetalhes = useMemo(() => {
    if (!eventoSlug) return null;
    const salvo = lerInscricaoCodigo();
    const id = Number(modalidadeId);
    if (salvo && salvo.slug === eventoSlug && salvo.modalidades.includes(id)) {
      return `/e/${eventoSlug}?c=${encodeURIComponent(salvo.codigo)}&ver=1`;
    }
    return `/e/${eventoSlug}?ver=1`;
  }, [eventoSlug, modalidadeId]);

  function preencherForm(p: Partial<PessoaForm>) {
    setForm({
      nome: p.nome || '',
      cpf: p.cpf ? mascaraCPF(p.cpf) : '',
      nascimento: p.nascimento ? formatarNascimento(p.nascimento) : '',
      telefone: p.telefone ? mascaraTelefone(p.telefone) : '',
      email: p.email || '',
      cep: p.cep ? mascaraCEP(p.cep) : '',
      endereco: p.endereco || '',
      complemento: p.complemento || '',
      bairro: p.bairro || '',
      cidade: p.cidade || '',
      uf: p.uf || '',
    });
  }

  async function carregarPessoas() {
    const { data } = await supabase
      .from('participantes')
      .select(
        'id, nome, cpf, nascimento, telefone, email, cep, endereco, complemento, bairro, cidade, uf, foto_url',
      )
      .eq('criado_por_id', uid)
      .is('usuario_id', null)
      .order('nome');
    setPessoas((data as PessoaSalva[]) || []);
  }

  async function escolherModo(m: 'eu' | 'outra') {
    setModo(m);
    setAvisoFalta(null);
    setDestaque(null);
    setErroGeral(null);
    setInscricaoDuplicada(null);
    setErroCampos({});
    setEuParticipante(null);
    setPessoaSelecionada(null);
    setNovoCadastro(false);
    setEditando(false);

    if (m === 'eu') {
      // Carrega o perfil único da pessoa (vw_meu_perfil) em vez de montar formulário.
      const { data: perfil } = await supabase
        .from('vw_meu_perfil')
        .select('*')
        .maybeSingle();
      const p = perfil as MeuPerfil | null;

      if (p && !p.cadastro_incompleto) {
        // Perfil completo: mostra só a conferência, com link para editar em /meu-perfil.
        setEuParticipante({
          id: p.participante_id ?? 0,
          nome: p.nome,
          cpf: p.cpf,
          nascimento: p.nascimento,
          telefone: p.telefone,
          email: p.email,
          endereco: p.endereco,
          complemento: p.complemento,
          bairro: p.bairro,
          cidade: p.cidade,
          uf: p.uf,
          foto_url: p.foto_url,
        });
        preencherForm({
          nome: p.nome,
          cpf: p.cpf || '',
          nascimento: p.nascimento || '',
          telefone: p.telefone || '',
          email: p.email || user?.email || '',
          cep: p.cep || '',
          endereco: p.endereco || '',
          complemento: p.complemento || '',
          bairro: p.bairro || '',
          cidade: p.cidade || '',
          uf: p.uf || '',
        });
        setPerfilIncompleto(false);
        return;
      }

      if (p) {
        // Cadastro incompleto: pede o que falta e salva via salvar_meu_perfil.
        preencherForm({
          nome: p.nome,
          cpf: p.cpf || '',
          nascimento: p.nascimento || '',
          telefone: p.telefone || '',
          email: p.email || user?.email || '',
          cep: p.cep || '',
          endereco: p.endereco || '',
          complemento: p.complemento || '',
          bairro: p.bairro || '',
          cidade: p.cidade || '',
          uf: p.uf || '',
        });
        setPerfilIncompleto(true);
        return;
      }

      // Conta antiga (sem cadastro ainda): parte do que a conta já tem.
      const { data: usu } = await supabase
        .from('usuarios')
        .select('nome, cpf, telefone')
        .eq('id', uid)
        .maybeSingle();
      preencherForm({
        nome:
          usu?.nome ||
          (user?.user_metadata?.full_name as string | undefined) ||
          (user?.user_metadata?.name as string | undefined) ||
          '',
        cpf: usu?.cpf || '',
        nascimento: '',
        telefone: usu?.telefone || '',
        email: user?.email || '',
      });
      setPerfilIncompleto(true);
    } else {
      setPerfilIncompleto(false);
      setForm(FORM_VAZIO);
      await carregarPessoas();
    }
  }

  function selecionarPessoa(p: PessoaSalva) {
    setPessoaSelecionada(p);
    setNovoCadastro(false);
    setEditando(false);
    preencherForm({
      nome: p.nome,
      cpf: p.cpf || '',
      nascimento: p.nascimento || '',
      telefone: p.telefone || '',
      email: p.email || '',
      cep: p.cep || '',
      endereco: p.endereco || '',
      complemento: p.complemento || '',
      bairro: p.bairro || '',
      cidade: p.cidade || '',
      uf: p.uf || '',
    });
  }

  function cadastrarOutra() {
    setPessoaSelecionada(null);
    setNovoCadastro(true);
    setEditando(false);
    setForm(FORM_VAZIO);
  }

  function editarEmConferencia() {
    setEditando(true);
  }

  function handleCampo(campo: keyof PessoaForm, valor: string) {
    let v = valor;
    if (campo === 'cpf') v = mascaraCPF(valor);
    if (campo === 'telefone') v = mascaraTelefone(valor);
    if (campo === 'cep') v = mascaraCEP(valor);
    if (campo === 'nascimento') v = mascaraData(valor);
    setForm((f) => ({ ...f, [campo]: v }));
    setErroCampos((e) => ({ ...e, [campo]: '' }));
    setAvisoFalta(null);
  }

  async function salvarParticipante(): Promise<number | null> {
    // Só nome e telefone são obrigatórios: o resto vai nulo quando vazio.
    const nascimentoISO =
      form.nascimento.replace(/\D/g, '').length === 8 ? dataParaISO(form.nascimento) : null;
    const dados = {
      nome: form.nome.trim(),
      cpf: form.cpf.replace(/\D/g, '') || null,
      nascimento: nascimentoISO || null,
      telefone: form.telefone.replace(/\D/g, '') || null,
      email: form.email.trim() || null,
    };

    // Endereço (também para "Outra pessoa", quando o bloco está visível).
    const enderecoDados = mostrarEndereco
      ? {
          cep: form.cep ? form.cep.replace(/\D/g, '') : null,
          endereco: form.endereco.trim() || null,
          complemento: form.complemento.trim() || null,
          bairro: form.bairro.trim() || null,
          cidade: form.cidade.trim() || null,
          uf: form.uf || null,
        }
      : {};

    if (modo === 'eu') {
      // Salva sempre via salvar_meu_perfil: cria/atualiza o participante com
      // usuario_id = uid e espelha nome/CPF/telefone na conta. Retorna o id.
      const { data, error: err } = await supabase.rpc('salvar_meu_perfil', {
        p_nome: dados.nome,
        p_cpf: dados.cpf,
        p_nascimento: dados.nascimento,
        p_telefone: dados.telefone,
        p_email: dados.email,
        p_cep: form.cep ? form.cep.replace(/\D/g, '') : null,
        p_endereco: form.endereco.trim() || null,
        p_complemento: form.complemento.trim() || null,
        p_bairro: form.bairro.trim() || null,
        p_cidade: form.cidade.trim() || null,
        p_uf: form.uf || null,
      });
      if (err) throw err;
      return (data as number) ?? null;
    }

    if (pessoaSelecionada) {
      const { error: err } = await supabase
        .from('participantes')
        .update({ ...dados, ...enderecoDados })
        .eq('id', pessoaSelecionada.id);
      if (err) throw err;
      return pessoaSelecionada.id;
    }
    const { data, error: err } = await supabase
      .from('participantes')
      .insert({ ...dados, ...enderecoDados, criado_por_id: uid })
      .select('id')
      .single();
    if (err) throw err;
    return data.id;
  }

  // O que falta para avançar, em linguagem simples.
  function calcularFalta(): { msg: string; alvo: string; bloco: string } | null {
    if (!modo) {
      return { msg: 'Falta escolher quem vai participar', alvo: 'bloco-quem', bloco: 'quem' };
    }
    if (!form.nome.trim()) {
      return { msg: 'Falta o nome', alvo: 'nome', bloco: 'pessoa' };
    }
    if (!form.telefone.replace(/\D/g, '')) {
      return { msg: 'Falta o telefone', alvo: 'telefone', bloco: 'pessoa' };
    }
    if (temGrupos && grupoId == null) {
      if (!duasEtapas) {
        return { msg: 'Falta escolher uma opção', alvo: 'bloco-grupo', bloco: 'grupo' };
      }
      if (!cidade) {
        return {
          msg: 'Falta escolher a cidade',
          alvo: 'bloco-cidade',
          bloco: 'cidade',
        };
      }
      const sexos = sexosDaCidade(grupos, cidade);
      return {
        msg: `Falta escolher: ${sexos.join(' ou ')}`,
        alvo: 'bloco-sexo',
        bloco: 'sexo',
      };
    }
    return null;
  }

  async function continuar() {
    if (salvando) return;

    // Ainda não escolheu quem vai participar: leva até a lista.
    if (modo === 'outra' && pessoas.length > 0 && !pessoaSelecionada && !novoCadastro) {
      setAvisoFalta('Falta escolher quem vai participar');
      destacar('bloco-pessoas', 'pessoas');
      return;
    }

    const erros: Record<string, string> = {};
    const digitosTelefone = form.telefone.replace(/\D/g, '');
    const digitosNascimento = form.nascimento.replace(/\D/g, '');

    if (!form.nome.trim()) erros.nome = 'Digite o nome de quem vai participar.';
    // Vazios passam. Só valida o formato do que a pessoa decidir preencher.
    if (form.cpf && !validarCPF(form.cpf)) erros.cpf = 'Verifique o CPF digitado.';
    if (digitosNascimento && digitosNascimento.length !== 8) {
      erros.nascimento = 'Digite a data completa, como 13/09/2000.';
    }
    if (!digitosTelefone) erros.telefone = 'Digite o telefone, com DDD.';
    else if (digitosTelefone.length !== 10 && digitosTelefone.length !== 11)
      erros.telefone = 'Digite o telefone com DDD, como (14) 99999-9999.';
    if (form.email.trim() && !EMAIL_REGEX.test(form.email.trim())) {
      erros.email = 'Digite um e-mail válido, como maria@gmail.com';
    }
    if (mostrarEndereco) {
      const digitosCep = form.cep.replace(/\D/g, '');
      if (digitosCep && digitosCep.length !== 8) erros.cep = 'Digite o CEP com 8 números.';
    }
    if (temGrupos && grupoId == null) erros.grupo = 'Escolha uma opção.';
    setErroCampos(erros);

    // 1º: o que falta escolher/preencher (nome, telefone, cidade, sexo).
    const falta = calcularFalta();
    if (falta) {
      setAvisoFalta(falta.msg);
      destacar(falta.alvo, falta.bloco);
      return;
    }

    // 2º: formato inválido de algum campo preenchido (CPF, telefone, e-mail...).
    if (Object.keys(erros).length > 0) {
      const [campo, texto] = Object.entries(erros)[0];
      setAvisoFalta(texto);
      const alvos: Record<string, string> = {
        nome: 'nome',
        cpf: 'cpf',
        nascimento: 'nascimento',
        telefone: 'telefone',
        email: 'email',
        cep: 'cep',
        grupo: 'bloco-grupo',
      };
      destacar(alvos[campo] ?? 'bloco-pessoa', 'pessoa');
      return;
    }

    setAvisoFalta(null);

    if (enviando.current) return;
    enviando.current = true;

    setSalvando(true);
    setErroGeral(null);
    setInscricaoDuplicada(null);
    try {
      const participanteId = await salvarParticipante();
      if (!participanteId) throw new Error('Não foi possível salvar os dados do participante.');

      const { data: duplicada } = await supabase
        .from('inscricoes')
        .select('id, codigo')
        .eq('modalidade_id', Number(modalidadeId))
        .eq('participante_id', participanteId)
        .neq('status', 'cancelada')
        .limit(1)
        .maybeSingle();
      if (duplicada) {
        setInscricaoDuplicada(duplicada.codigo);
        return;
      }

      if (temGrupos && (!grupoSelecionado || grupoSelecionado.situacao !== 'abertas')) {
        setErroGeral('As vagas para esta opção acabaram.');
        return;
      }

      if (temFormulario) {
        const query = `participante=${participanteId}${grupoId != null ? `&opcao=${grupoId}` : ''}`;
        navigate(`/inscricao/${modalidadeId}/ficha?${query}`);
        return;
      }

      const { data, error: err } = await supabase
        .from('inscricoes')
        .insert({
          evento_id: partes[0].evento_id,
          modalidade_id: Number(modalidadeId),
          opcao_id: grupoId ?? null,
          participante_id: participanteId,
          responsavel_id: uid,
        })
        .select('codigo')
        .single();
      if (err) throw err;
      navigate(`/minha-inscricao/${data.codigo}?nova=1`);
    } catch (e) {
      setErroGeral(mensagemAmigavel(e, 'Não conseguimos continuar. Tente de novo.'));
    } finally {
      enviando.current = false;
      setSalvando(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background-100">
        <div className="mx-auto w-full max-w-[720px] px-4 pb-28 pt-6">
          <div className="h-1.5 w-full animate-pulse rounded-full bg-secondary-200" />
          <div className="mt-4 h-16 w-full animate-pulse rounded-lg bg-secondary-100" />
          <div className="mt-4 h-40 w-full animate-pulse rounded-lg bg-secondary-100" />
          <div className="mt-4 h-64 w-full animate-pulse rounded-lg bg-secondary-100" />
        </div>
      </div>
    );
  }

  if (erro) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-4 py-8">
        <div className="w-full max-w-[420px] overflow-hidden rounded-lg border border-secondary-200 bg-white">
          <div className="h-[10px] w-full bg-primary-500" />
          <div className="p-6 text-center">
            <h1 className="text-xl font-normal text-foreground-950">Não foi possível continuar</h1>
            <p className="mt-2 text-sm text-secondary-500">{erro}</p>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="mt-6 w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-primary-600"
            >
              Ver eventos disponíveis
            </button>
          </div>
        </div>
      </div>
    );
  }

  const mostrarListaPessoas =
    modo === 'outra' && pessoas.length > 0 && !pessoaSelecionada && !novoCadastro;

  // Borda vermelha por 2 s no bloco que faltou.
  const marca = (alvo: string) =>
    destaque === alvo ? 'rounded-lg ring-2 ring-danger-500' : '';

  const pessoaEmConferencia = modo === 'eu' ? euParticipante : pessoaSelecionada;
  const mostrarConferencia = !!pessoaEmConferencia && !editando && !mostrarListaPessoas;
  const mostrarForm = !!modo && !mostrarListaPessoas && !mostrarConferencia;

  return (
    <div className="min-h-screen bg-background-100">
      <div className="mx-auto w-full max-w-[720px] px-4 pb-28 pt-6">
        <BarraProgresso total={temFormulario ? 3 : 2} ativa={0} />

        <div className="mt-4 space-y-4">
          <CartaoResumo
            evento={eventoNome}
            modalidade={partes[0]?.modalidade || ''}
            grupo={grupoSelecionado?.grupo || null}
            valor={valorExibido}
          />

          {linkDetalhes && (
            <Link
              to={linkDetalhes}
              className="aperta inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap px-1 text-[13px] font-semibold text-secondary-500 transition-colors hover:text-primary-600"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-information-line" aria-hidden />
              </span>
              Ver detalhes do evento
            </Link>
          )}

          <div id="bloco-quem" className={marca('quem')}>
            <CartaoQuemVai valor={modo} onChange={escolherModo} />
          </div>

          {mostrarListaPessoas && (
            <div id="bloco-pessoas" className={marca('pessoas')}>
              <div className="rounded-lg border border-secondary-200 bg-white p-6">
                <h2 className="text-base font-medium text-foreground-950">Quem você vai inscrever?</h2>
                <p className="mt-1 text-sm text-secondary-500">
                  Estas são as pessoas que você já cadastrou antes.
                </p>
                <div className="mt-3 space-y-1">
                  {pessoas.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => selecionarPessoa(p)}
                      className="flex w-full cursor-pointer items-center justify-between rounded-md px-3 py-3 text-left transition-colors hover:bg-background-100"
                    >
                      <span className="min-w-0">
                        <span className="block text-[15px] font-medium text-foreground-950">
                          {p.nome}
                        </span>
                        {p.nascimento && (
                          <span className="block text-sm text-secondary-500">
                            {formatarNascimento(p.nascimento)}
                          </span>
                        )}
                      </span>
                      <span className="flex h-5 w-5 items-center justify-center text-primary-600">
                        <i className="ri-arrow-right-s-line text-xl" />
                      </span>
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={cadastrarOutra}
                    className="flex w-full cursor-pointer items-center gap-3 rounded-md px-3 py-3 text-left transition-colors hover:bg-background-100"
                  >
                    <span className="flex h-5 w-5 items-center justify-center text-primary-600">
                      <i className="ri-add-line text-xl" />
                    </span>
                    <span className="text-[15px] font-medium text-primary-600">
                      Cadastrar outra pessoa
                    </span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {mostrarConferencia && pessoaEmConferencia && (
            <CartaoConferencia
              pessoa={pessoaEmConferencia}
              onEditar={editarEmConferencia}
              linkEditar={modo === 'eu' ? '/meu-perfil' : undefined}
            />
          )}

          {mostrarForm && (
            <div id="bloco-pessoa" className={marca('pessoa')}>
              <CartaoPessoa
                form={form}
                modo={modo}
                mostrarEndereco={mostrarEndereco}
                erros={erroCampos}
                onChange={handleCampo}
              />
            </div>
          )}

          {temGrupos && (
            <CartaoGrupo
              grupos={grupos}
              selecionado={grupoId}
              onChange={escolherGrupo}
              cidade={cidade}
              onEscolherCidade={escolherCidade}
              destaque={
                destaque === 'cidade' || destaque === 'sexo' || destaque === 'grupo'
                  ? destaque
                  : null
              }
            />
          )}

          {erroGeral && (
            <div className="rounded-md border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
              {erroGeral}
            </div>
          )}

          {inscricaoDuplicada && (
            <div className="rounded-md border border-danger-200 bg-danger-50 px-4 py-3">
              <p className="text-sm text-danger-700">Esta pessoa já está inscrita nesta opção.</p>
              <button
                type="button"
                onClick={() => navigate(`/minha-inscricao/${inscricaoDuplicada}`)}
                className="mt-2 cursor-pointer whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
              >
                Ver a inscrição
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="fixed inset-x-0 bottom-0 border-t border-secondary-200 bg-white px-4 py-3">
        <div className="mx-auto w-full max-w-[720px]">
          {avisoFalta && (
            <p
              className="mb-2 flex items-start gap-1.5 text-sm font-medium text-danger-600"
              role="alert"
              aria-live="polite"
            >
              <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
                <i className="ri-error-warning-line" aria-hidden />
              </span>
              <span>{avisoFalta}</span>
            </p>
          )}
          <button
            type="button"
            onClick={continuar}
            className="flex h-12 w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-md bg-primary-500 text-[15px] font-medium text-white transition-colors hover:bg-primary-600"
          >
            {salvando ? 'Salvando...' : 'Continuar'}
          </button>
        </div>
      </div>
    </div>
  );
}